import { TableSortDirection } from "@amzn/meridian/table/table";
import { createAsyncThunk, createSlice, PayloadAction } from "@reduxjs/toolkit";
import { DEFAULT_TABLE_PAGE_SIZE } from "src/constants/constants";
import adminService from "../service/adminService";
import { ConversationSummary } from "../types";

interface AdminState {
  visitedConversations: string[];
  searchField: string;
  paginationState: PaginationData;
  sortState: SortData<ConversationSummary>;
  conversations: ConversationSummary[];
}

interface SortData<T> {
  sortColumn: keyof T;
  sortDirection: TableSortDirection;
}

interface PaginationData {
  perPage: number;
  currentPage: number;
  totalPages: number;
}

const initialState: AdminState = {
  visitedConversations: [],
  searchField: "",
  paginationState: {
    currentPage: 1,
    perPage: DEFAULT_TABLE_PAGE_SIZE,
    totalPages: null,
  },
  sortState: {
    sortColumn: "lastMessageTime",
    sortDirection: "descending",
  },
  conversations: [],
};

/**
 * get conversations
 */
export const getConversations = createAsyncThunk(
  "admin/getConversations",
  async () => {
    return adminService.getConversations();
  },
);

/** Admin Slice */
const { reducer, actions } = createSlice({
  name: "adminSlice",
  initialState,
  reducers: {
    visitConversation: (state, action: PayloadAction<string>) => {
      state.visitedConversations.push(action.payload);
    },
    setSortState: (
      state,
      action: PayloadAction<Partial<SortData<ConversationSummary>>>,
    ) => {
      state.sortState = { ...state.sortState, ...action.payload };
      state.paginationState.currentPage = 1;
    },
    setPaginationState: (
      state,
      action: PayloadAction<Partial<PaginationData>>,
    ) => {
      state.paginationState = { ...state.paginationState, ...action.payload };
    },
    setSearchField: (state, action: PayloadAction<string>) => {
      state.searchField = action.payload;
      state.paginationState.currentPage = 1;
    },
  },
  extraReducers: (builder) => {
    // Add reducers for additional action types here, and handle loading state as needed
    builder.addCase(getConversations.fulfilled, (state, { payload }) => {
      state.conversations = payload;
    });
  },
});

export const {
  visitConversation,
  setPaginationState,
  setSearchField,
  setSortState,
} = actions;

export default reducer;
